import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Plus, Pencil, Eye, Trash2, ExternalLink, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminTitle } from "@/admin/AdminLayout";
import ArticleEditor from "@/admin/ArticleEditor";
import { useT } from "@/lib/labels";
import { useSite } from "@/context/SiteContext";

export const STATUS = {
  draft: { label: "Draft", cls: "text-[#5e4a35] border-[#5e4a35]/40" },
  pending: { label: "Awaiting approval", cls: "text-[#1e4976] border-[#1e4976]/50" },
  approved: { label: "Published", cls: "text-[#2f6b3a] border-[#2f6b3a]/50" },
  rejected: { label: "Returned", cls: "text-[#991b1b] border-[#991b1b]/50" },
};

export const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.draft;
  return <span data-testid={`status-${status}`} className={`inline-block px-2 py-0.5 border font-heading text-[11px] font-bold uppercase tracking-wider ${s.cls}`}>{s.label}</span>;
};

const Guard = ({ children }) => {
  const { user } = useAuth();
  const { settings } = useSite();
  if (user === null) return <p className="p-20 text-center font-display text-2xl">Opening…</p>;
  if (!user) return <Navigate to="/join" replace />;
  if (user.role !== "admin" && !user.is_master && !settings.readers_enabled) return <p data-testid="contribute-closed" className="max-w-2xl mx-auto p-20 text-center font-display text-2xl">Reader contributions are currently closed.</p>;
  return <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">{children}</div>;
};

export const ContributeEditor = () => <Guard><ArticleEditor mode="reader" /></Guard>;

const Row = ({ a, onDelete }) => (
  <div data-testid={`my-article-${a.slug}`} className="flex flex-wrap items-center gap-4 px-6 py-4 border-b border-[#c9a227]/25">
    <div className="flex-1 min-w-[14rem]">
      <p className="font-heading font-bold">{a.title}</p>
      <p className="text-xs text-[#5e4a35]">Updated {new Date(a.updated_at).toLocaleString()}</p>
      {a.review_status === "rejected" && a.review_note && <p className="text-sm italic text-[#991b1b] mt-1">Lorekeeper's note: {a.review_note}</p>}
    </div>
    <StatusBadge status={a.review_status} />
    <div className="flex gap-1">
      <Link to={`/preview/${a.id}`} data-testid={`my-preview-${a.slug}`} className="p-2 hover:bg-[#c9a227]/20" title="Preview"><Eye className="w-4 h-4" /></Link>
      {a.review_status === "approved"
        ? <Link to={`/wiki/${a.slug}`} className="p-2 hover:bg-[#c9a227]/20" title="View published page"><ExternalLink className="w-4 h-4" /></Link>
        : <>
          <Link to={`/contribute/${a.id}`} data-testid={`my-edit-${a.slug}`} className="p-2 hover:bg-[#c9a227]/20" title="Edit"><Pencil className="w-4 h-4" /></Link>
          <button onClick={() => onDelete(a)} data-testid={`my-delete-${a.slug}`} className="p-2 text-[#991b1b] hover:bg-[#991b1b]/10" title="Delete"><Trash2 className="w-4 h-4" /></button>
        </>}
    </div>
  </div>
);

const MyScrolls = () => {
  const { user, logout } = useAuth();
  const [items, setItems] = useState(null);
  const t = useT();
  const load = () => api.get("/reader/articles").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try { await api.delete(`/reader/articles/${a.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  return (
    <div data-testid="contribute-page">
      <AdminTitle eyebrow={`Welcome, ${user.name || "reader"}`} title={t("scrolls.title")}>
        <Link to="/contribute/new" data-testid="contribute-new-article" className="btn-illum theme-flame"><Plus className="w-4 h-4" /> {t("scrolls.new")}</Link>
        {user.profile_slug && t("scrolls.profile") && <Link to={`/reader/${user.profile_slug}`} data-testid="contribute-my-profile" className="btn-ghost-illum"><UserRound className="w-4 h-4" /> {t("scrolls.profile")}</Link>}
        <button onClick={logout} data-testid="contribute-logout" className="btn-ghost-illum">{t("scrolls.sign_out")}</button>
      </AdminTitle>
      <p className="mb-6 italic text-[#4a3a2a]">Your articles stay private until you submit them and the Lorekeeper approves them for the Readers' Chronicles.</p>
      <div className="vellum">
        {items?.map((a) => <Row key={a.id} a={a} onDelete={remove} />)}
        {items?.length === 0 && <p data-testid="contribute-empty" className="p-8 text-center italic">No scrolls yet — begin your first tale.</p>}
      </div>
    </div>
  );
};

export default function ContributePage() {
  return <Guard><MyScrolls /></Guard>;
}
